import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { cn } from '@avanta/shared/utils/cn';
import { FormProvider, useForm, useFormContext, useWatch } from 'react-hook-form';
import { Navigate } from 'react-router';
import { ROUTES } from 'constants/routes';
import { useFetchActivePatientRecord } from 'hooks/useFetchActivePatientRecord';
import { TopNav } from 'pages/AppointmentsReserve/components/ui/TopNav';
import { useBookAppointment } from 'pages/AppointmentsReserve/hooks/useBookAppointment';
import { useEditAppointment } from 'pages/AppointmentsReserve/hooks/useEditAppointment';
import { useFetchInsuredCenters } from 'pages/AppointmentsReserve/hooks/useFetchInsuredCenters';
import { useReserveLocationState } from 'pages/AppointmentsReserve/hooks/useReserveLocationState';
import { useStepNavigation } from 'pages/AppointmentsReserve/hooks/useStepNavigation';
import { useSyncSessionDefaults } from 'pages/AppointmentsReserve/hooks/useSyncSessionDefaults';
import { type PatientRecord } from 'types/patient';
import { AppointmentsReservePayload } from './constants';
import { type AppointmentsReserveFormData } from './types';
import { buildBookPayload, buildEditPayload, getDefaultValues } from './utils';

interface AppointmentsReserveFormProviderProps {
  patientRecord?: PatientRecord;
}

const AppointmentsReserveForm = (): React.ReactElement => {
  const { control, handleSubmit } = useFormContext<AppointmentsReserveFormData>();
  const flow = useWatch({ control, name: AppointmentsReservePayload.flow });
  const { steps, currentStepIndex, currentStep, handleNext, handleBack } = useStepNavigation();
  const StepComponent = currentStep.component;
  const { mutateAsync: bookAppointment } = useBookAppointment();
  const { mutateAsync: editAppointment } = useEditAppointment();
  const { centersOptions } = useFetchInsuredCenters();
  const { appointment } = useReserveLocationState();
  useSyncSessionDefaults();

  const onSubmit = async (data: AppointmentsReserveFormData): Promise<void> => {
    if (flow === FLOW_MODES.edit) {
      await editAppointment({
        payload: buildEditPayload(data, appointment?.dateTime ?? ''),
        document: data.document,
      });
      return;
    }
    await bookAppointment({
      payload: buildBookPayload(data),
      document: data.document,
      patient: data.patient,
      psychologist: data.psychologist,
      center: centersOptions.find(center => center.value === data.backofficeCenterId),
    });
  };

  return (
    <div className="flex flex-col h-dvh overflow-hidden relative pb-6">
      <TopNav steps={steps} currentStep={currentStepIndex + 1} />
      <div className="scrollbar-custom min-h-0 flex-1 overflow-y-auto">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className={cn(
            'mx-auto flex flex-col items-center',
            flow === FLOW_MODES.search_facultative && 'h-full'
          )}
        >
          <StepComponent onNext={handleNext} onBack={handleBack} />
        </form>
      </div>
    </div>
  );
};

const AppointmentsReserveFormProvider = ({
  patientRecord,
}: AppointmentsReserveFormProviderProps): React.ReactElement => {
  const state = useReserveLocationState();
  const methods = useForm<AppointmentsReserveFormData>({
    defaultValues: getDefaultValues({ state, patientRecord }),
  });
  return (
    <FormProvider {...methods}>
      <AppointmentsReserveForm />
    </FormProvider>
  );
};

export const AppointmentsReserve = (): React.ReactElement => {
  const { patient, flow } = useReserveLocationState();
  const { data: patientRecord, isLoading } = useFetchActivePatientRecord(patient?.id ?? '');

  if (!patient && flow !== FLOW_MODES.search_patient) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  if (isLoading) return <LoadingScreen />;

  return <AppointmentsReserveFormProvider patientRecord={patientRecord} />;
};
